import { resolveId } from '@medplum/core'
import type {
  Attachment,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Reference,
} from '@medplum/fhirtypes'
import { flatMap } from 'lodash-es'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { codeFromCodeableConcept } from '#src/codeableConcept.ts'
import { Extension } from '#src/decorators.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import {
  buildInitialResponseItem,
  buildInitialResponseItems,
} from '#src/questionnaire.ts'
import type { QueryTypes } from '#src/search.ts'
import { QUESTIONNAIRE_RESPONSE_NAME_EXTENSION_URL } from '#src/structureDefinitions.ts'
import { canonical, isOfType, now } from '#src/utils.ts'

export class ExtendedQuestionnaireResponse extends ExtendedResource<QuestionnaireResponse> {
  static readonly RESOURCE_TYPE = 'QuestionnaireResponse'

  constructor(data?: Omit<QuestionnaireResponse, 'resourceType'>) {
    super({
      status: 'completed',
      authored: now(),
      ...data,
    })
  }

  get authored(): string {
    invariant(
      this.resource.authored,
      `${this.reference.reference} must have an authored date`
    )
    return this.resource.authored
  }

  set author(practitioner) {
    this.resource.author = practitioner
  }

  get author(): Reference<Practitioner> | undefined {
    invariant(
      this.resource.author === undefined ||
        isOfType(this.resource.author, 'Practitioner'),
      `${this.reference.reference} author must be a Practitioner`
    )
    return this.resource.author
  }

  set subjectPatient(patient) {
    this.resource.subject = patient
  }

  get subjectPatient(): Reference<Patient> {
    invariant(
      this.resource.subject && isOfType(this.resource.subject, 'Patient'),
      `${this.reference.reference} subject must be a Patient`
    )
    return this.resource.subject
  }

  @Extension({
    url: QUESTIONNAIRE_RESPONSE_NAME_EXTENSION_URL,
    kind: 'valueString',
  })
  name?: string

  getAnswer(linkId: string): QuestionnaireResponseItemAnswer | undefined {
    const item = this.flatItems.find((item) => item.linkId === linkId)
    return item?.answer?.[0]
  }

  getAnswerValue(
    linkId: string
  ): string | number | boolean | Attachment | undefined {
    const answer = this.getAnswer(linkId)
    return match(answer)
      .when(
        (a) => a === undefined,
        () => undefined
      )
      .when(
        (a) => a.valueCoding !== undefined,
        (a) => a.valueCoding?.code
      )
      .when(
        (a) => a.valueBoolean !== undefined,
        (a) => a.valueBoolean
      )
      .when(
        (a) => a.valueDecimal !== undefined,
        (a) => a.valueDecimal
      )
      .when(
        (a) => a.valueInteger !== undefined,
        (a) => a.valueInteger
      )
      .when(
        (a) => a.valueDate !== undefined,
        (a) => a.valueDate
      )
      .when(
        (a) => a.valueDateTime !== undefined,
        (a) => a.valueDateTime
      )
      .when(
        (a) => a.valueTime !== undefined,
        (a) => a.valueTime
      )
      .when(
        (a) => a.valueString !== undefined,
        (a) => a.valueString
      )
      .when(
        (a) => a.valueUri !== undefined,
        (a) => a.valueUri
      )
      .when(
        (a) => a.valueAttachment !== undefined,
        (a) => a.valueAttachment
      )
      .when(
        (a) => a.valueQuantity !== undefined,
        (a) => a.valueQuantity
      )
      .when(
        (a) => a.valueReference !== undefined,
        (a) => a.valueReference
      )
      .otherwise(() => {
        throw new Error(
          `Unsupported answer value for ${linkId} on ${this.reference.reference}`
        )
      })
  }

  getAnswerValueNumber(linkId: string): number | null {
    const answer = this.getAnswer(linkId)
    return match(answer)
      .when(
        (a) => a === undefined,
        () => null
      )
      .when(
        (a) => a.valueDecimal !== undefined,
        (a) => a.valueDecimal ?? null
      )
      .when(
        (a) => a.valueInteger !== undefined,
        (a) => a.valueInteger ?? null
      )
      .otherwise(() => {
        throw new Error(
          `Unsupported number answer value for ${linkId} on ${this.reference.reference}`
        )
      })
  }

  setAnswer(questionnaireItem: QuestionnaireItem, value: any): void {
    let item = this.flatItems.find((i) => i.linkId === questionnaireItem.linkId)
    if (!item) {
      item = buildInitialResponseItem(questionnaireItem)
      this.resource.item?.push(item)
    }

    let maybeValue = value
    // Treat empty or whitespace-only strings as undefined (remove answer)
    if (typeof value === 'string' && value.trim() === '') {
      maybeValue = undefined
    }

    if (maybeValue === undefined || maybeValue === null) {
      item.answer = undefined
      return
    }

    if (questionnaireItem.answerOption) {
      const answer = questionnaireItem.answerOption.find(
        (option) => option.valueCoding?.code === value
      )
      invariant(answer, `${value} not found as an option for ${item.linkId}`)
      item.answer = [answer]
      return
    }

    const answer = match(questionnaireItem.type)
      .with('boolean', () => ({ valueBoolean: z.boolean().parse(value) }))
      .with('decimal', () => ({ valueDecimal: z.number().parse(value) }))
      .with('integer', () => ({ valueInteger: z.number().int().parse(value) }))
      .with('date', () => ({ valueDate: z.string().date().parse(value) }))
      .with('dateTime', () => ({
        valueDateTime: z.string().datetime().parse(value),
      }))
      .with('time', () => ({ valueTime: z.string().time().parse(value) }))
      .with('string', 'text', () => ({ valueString: z.string().parse(value) }))
      .with('url', () => ({ valueUri: z.string().url().parse(value) }))
      .with('attachment', () => ({ valueAttachment: z.any().parse(value) })) // TODO: Add attachment schema
      .with('quantity', () => ({ valueQuantity: z.any().parse(value) })) // TODO: Add quantity schema
      .with('reference', () => ({ valueReference: z.any().parse(value) })) // TODO: Add reference schema
      .otherwise(() => {
        throw new Error(
          `Unsupported questionnaire item type ${questionnaireItem.type} for ${item.linkId}`
        )
      })
    item.answer = [answer]
  }

  get flatItems(): QuestionnaireResponseItem[] {
    return flatMap(
      this.resource.item,
      ExtendedQuestionnaireResponse.flattenItem
    )
  }

  private static flattenItem(
    item: QuestionnaireResponseItem | undefined
  ): QuestionnaireResponseItem[] {
    if (!item) {
      return []
    }
    return item.item
      ? [item, ...flatMap(item.item, ExtendedQuestionnaireResponse.flattenItem)]
      : [item]
  }

  static searchQuery({
    questionnaire,
    subject,
  }: {
    questionnaire: Questionnaire | string
    subject: Reference
  }): QueryTypes {
    return {
      questionnaire:
        typeof questionnaire === 'string'
          ? questionnaire
          : canonical({
              url: questionnaire.url,
            }),
      status: 'completed',
      subject,
      _sort: '-authored',
    }
  }

  static queryByIds(ids: string[]): QueryTypes {
    return {
      _id: ids,
      'status:not': 'entered-in-error',
      _count: ids.length,
      _sort: '-authored',
    }
  }

  static buildFromQuestionnaire<T extends ExtendedQuestionnaireResponse>(
    this: new (...args: any[]) => T,
    {
      questionnaire,
      subject,
      author,
      status,
    }: {
      questionnaire: Questionnaire
      subject: Reference<Patient | Practitioner>
      author?: Reference<Practitioner>
      status?: QuestionnaireResponse['status']
    }
  ): T {
    const response = new this({
      questionnaire: canonical({
        url: questionnaire.url,
      }),
      subject,
      status: status ?? 'completed',
      author,
      item: buildInitialResponseItems(questionnaire.item),
    })
    return response
  }

  static buildFromQuestionnaireResponse<
    T extends ExtendedQuestionnaireResponse,
  >(
    this: new (...args: any[]) => T,
    {
      questionnaireResponse,
      author,
      status,
    }: {
      questionnaireResponse: ExtendedQuestionnaireResponse
      author: Reference<Practitioner>
      status: QuestionnaireResponse['status']
    }
  ): T {
    return new this({
      language: questionnaireResponse.resource.language,
      text: questionnaireResponse.resource.text,
      basedOn: questionnaireResponse.resource.basedOn,
      partOf: questionnaireResponse.resource.partOf,
      questionnaire: questionnaireResponse.resource.questionnaire,
      status,
      subject: questionnaireResponse.resource.subject,
      encounter: questionnaireResponse.resource.encounter,
      author,
      source: questionnaireResponse.resource.source,
      item: questionnaireResponse.resource.item,
    })
  }

  get status(): QuestionnaireResponse['status'] {
    return this.resource.status
  }

  set status(status: QuestionnaireResponse['status']) {
    this.resource.status = status
  }

  get questionnaireUrl(): string | undefined {
    return this.resource.questionnaire
  }

  get authorDisplay(): string | undefined {
    return this.author?.display
  }

  get itemSummary(): {
    linkId: string
    answer: string | number | boolean | Attachment | undefined
  }[] {
    return this.flatItems.map((item) => ({
      linkId: item.linkId,
      answer: this.getAnswerValue(item.linkId),
    }))
  }

  get sourceCode(): string | undefined {
    return codeFromCodeableConcept(this.resource.source)
  }

  get basedOnDisplay(): string[] {
    return this.resource.basedOn?.map((r) => r.display ?? '') ?? []
  }

  get partOfDisplay(): string[] {
    return this.resource.partOf?.map((r) => r.display ?? '') ?? []
  }

  private getOrdinalValue(item: QuestionnaireResponseItem): number | undefined {
    const answer = item.answer?.[0]
    if (!answer?.extension) {
      return undefined
    }

    const ordinalExt = answer.extension.find(
      (ext) =>
        ext.url === 'http://hl7.org/fhir/StructureDefinition/ordinalValue'
    )
    return ordinalExt?.valueDecimal
  }

  protected getExportFields() {
    const itemValues = Object.fromEntries(
      this.flatItems.map((item) => {
        const ordinalValue = this.getOrdinalValue(item)
        const value = ordinalValue ?? this.getAnswerValue(item.linkId)
        return [item.linkId, value]
      })
    )

    return {
      id: this.id,
      status: this.status,
      name: this.name,
      questionnaire: this.questionnaireUrl,
      authored: this.authored,
      patientId: resolveId(this.subjectPatient),
      authorId: resolveId(this.author),
      authorDisplay: this.authorDisplay,
      items: this.itemSummary,
      itemValues,
      source: this.sourceCode,
      basedOn: this.basedOnDisplay,
      partOf: this.partOfDisplay,
    }
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(): QueryTypes {
    return {
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }
}

export interface QuestionnaireWithResponse {
  questionnaire: Questionnaire
  questionnaireResponse: ExtendedQuestionnaireResponse | null
}
